// useEntitlements — the tenant's active product entitlements, for dynamic product
// visibility in the app (E4). Reads /tenants/me/features (the enabled entitlement codes).
//
// IMPORTANT: this is UX only — the gateway + services enforce (E3). And it FAILS OPEN:
// a tenant with NO entitlement data yet (not provisioned) sees everything its RBAC allows,
// so we never hide a product area from an un-migrated tenant. Only a tenant that HAS
// entitlements but not this product gets it hidden.
import { useEffect, useState } from 'react';
import { getMyTenantFeatures } from '@/lib/hrApi';

// Catalogue product code → known legacy aliases (mirror of auth.products metadata.aliases
// for the product areas the app gates). Lets hasProduct() match whether the tenant was
// entitled with the catalogue code or a legacy feature code.
const ALIASES: Record<string, string[]> = {
  learning: ['training', 'training_module', 'lnd'],
  payroll: ['payroll_module', 'payrollProcessing', 'payslipGeneration'],
  credit: ['lending', 'loans'],
  benefits: ['benefits_management', 'benefits_module'],
  leave: ['leave_module', 'leaveCalendar', 'attendance'],
  performance: ['performance_reviews', 'performanceReviews'],
  developer_platform: ['apiV2', 'api_access'],
};

interface Entitlements {
  loaded: boolean;
  hasAny: boolean; // whether the tenant has ANY entitlement rows (drives fail-open)
  hasProduct: (code: string) => boolean;
  // hasProductStrict never fails open: returns true ONLY if the code (or an
  // alias) is explicitly enabled. Use for paid add-ons that must default OFF.
  hasProductStrict: (code: string) => boolean;
}

let cache: Set<string> | null = null; // module-level cache (per session)

// invalidateEntitlements clears the session cache so the next mount refetches
// (call after toggling a feature so nav/gates update).
export function invalidateEntitlements() {
  cache = null;
}

export function useEntitlements(): Entitlements {
  const [codes, setCodes] = useState<Set<string> | null>(cache);
  const [loaded, setLoaded] = useState<boolean>(cache !== null);

  useEffect(() => {
    if (cache !== null) return;
    let alive = true;
    getMyTenantFeatures()
      .then((rows) => {
        const set = new Set((rows ?? []).filter((r) => r.isEnabled).map((r) => r.featureCode));
        cache = set;
        if (alive) {
          setCodes(set);
          setLoaded(true);
        }
      })
      .catch(() => {
        // On failure, fail open (empty set → hasAny false → everything shows).
        cache = new Set();
        if (alive) {
          setCodes(cache);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const set = codes ?? new Set<string>();
  const hasAny = set.size > 0;

  return {
    loaded,
    hasAny,
    hasProduct: (code: string) => {
      if (!hasAny) return true; // fail open for un-provisioned tenants
      if (set.has(code)) return true;
      return (ALIASES[code] ?? []).some((a) => set.has(a));
    },
    hasProductStrict: (code: string) => {
      if (set.has(code)) return true;
      return (ALIASES[code] ?? []).some((a) => set.has(a));
    },
  };
}
